import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Building2, MapPin, User, CheckCircle } from "lucide-react";
import { COUNTRIES, getCountiesForCountry } from "../constants/locations";

const CompanyApplicationForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Company Info
    company_name: "",
    website: "",
    linkedin: "",
    instagram: "",
    twitter: "",
    facebook: "",
    
    // Contact Details
    contact_name: "",
    contact_role: "",
    contact_email: "",
    contact_phone: "",
    
    // Company Profile
    organisation_type: "",
    year_established: "",
    number_of_employees: "",
    annual_budget: "",
    
    // Address
    country: "",
    region: "",
    city: "",
    address: "",
    postal_code: ""
  });

  const [regions, setRegions] = useState([]);
  const [showRegionInput, setShowRegionInput] = useState(false);
  
  // Consent checkboxes
  const [consents, setConsents] = useState({
    accurate: false,
    gdpr: false,
    noGuarantee: false,
    terms: false
  });

  const handleCountryChange = (country) => {
    setFormData({ ...formData, country, region: "" });
    const countryRegions = getCountiesForCountry(country);
    
    if (countryRegions && countryRegions.length > 0) {
      setRegions(countryRegions);
      setShowRegionInput(false);
    } else {
      setRegions([]);
      setShowRegionInput(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate consents
    if (!consents.accurate || !consents.gdpr || !consents.noGuarantee || !consents.terms) {
      toast.error("Please accept all consent declarations");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/company-application/submit`, {
        ...formData,
        consents
      });
      
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="text-center">
            <CardHeader>
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <CardTitle className="text-2xl text-green-700">Application Submitted!</CardTitle>
              <CardDescription className="text-lg">
                Thank you for your interest in PlayBack Token. We'll review your application and be in touch soon.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate("/")} className="bg-gradient-to-r from-orange-500 to-red-500">
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/" className="text-red-600 hover:text-red-700 flex items-center gap-2">
            ← Back to Home
          </Link>
        </div>

        <Card>
          <CardHeader className="text-center border-b bg-gradient-to-r from-orange-50 to-red-50">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center mb-4 shadow-lg">
              <Building2 className="w-12 h-12 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Company Application
            </CardTitle>
            <CardDescription className="text-base mt-2">
              Apply to purchase PlayBack Tokens and support community volunteer hours
            </CardDescription>
          </CardHeader>

          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Basic Information */}
              <div className="bg-orange-50 rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-2 text-orange-700 font-semibold mb-4">
                  <Building2 className="w-5 h-5" />
                  <h3>Basic Information</h3>
                </div>

                <div>
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="e.g., Acme Corporation Ltd"
                  />
                </div>

                <div>
                  <Label htmlFor="website">Company Website *</Label>
                  <Input
                    id="website"
                    type="text"
                    required
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.yourcompany.com"
                  />
                </div>

                <div>
                  <Label className="mb-3 block">Company Social Media</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="LinkedIn URL"
                      value={formData.linkedin}
                      onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                    />
                    <Input
                      placeholder="Instagram URL"
                      value={formData.instagram}
                      onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    />
                    <Input
                      placeholder="Twitter/X URL"
                      value={formData.twitter}
                      onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                    />
                    <Input
                      placeholder="Facebook URL"
                      value={formData.facebook}
                      onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Details */}
              <div className="bg-green-50 rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-2 text-green-700 font-semibold mb-4">
                  <User className="w-5 h-5" />
                  <h3>Contact Details</h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_name">Contact Name *</Label>
                    <Input
                      id="contact_name"
                      required
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_role">Contact Role / Position *</Label>
                    <Input
                      id="contact_role"
                      required
                      value={formData.contact_role}
                      onChange={(e) => setFormData({ ...formData, contact_role: e.target.value })}
                      placeholder="e.g., CSR Manager"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact_email">Contact Email *</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    required
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="john.smith@company.com"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_phone">Contact Phone Number *</Label>
                  <Input
                    id="contact_phone"
                    type="tel"
                    required
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="+353 86 123 4567"
                  />
                </div>
              </div>

              {/* Company Profile */}
              <div className="bg-purple-50 rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-2 text-purple-700 font-semibold mb-4">
                  <Building2 className="w-5 h-5" />
                  <h3>Company Profile</h3>
                </div>

                <div>
                  <Label htmlFor="organisation_type">Type of Organisation *</Label>
                  <Select value={formData.organisation_type} onValueChange={(value) => setFormData({ ...formData, organisation_type: value })} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Private Company">Private Company</SelectItem>
                      <SelectItem value="PLC">PLC (Public Limited Company)</SelectItem>
                      <SelectItem value="NGO">NGO (Non-Governmental Organisation)</SelectItem>
                      <SelectItem value="Charity">Charity</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="year_established">Year Established *</Label>
                    <Input
                      id="year_established"
                      type="number"
                      required
                      value={formData.year_established}
                      onChange={(e) => setFormData({ ...formData, year_established: e.target.value })}
                      placeholder="e.g., 2010"
                    />
                  </div>

                  <div>
                    <Label htmlFor="number_of_employees">Number of Employees *</Label>
                    <Select value={formData.number_of_employees} onValueChange={(value) => setFormData({ ...formData, number_of_employees: value })} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-10">1-10</SelectItem>
                        <SelectItem value="11-50">11-50</SelectItem>
                        <SelectItem value="51-200">51-200</SelectItem>
                        <SelectItem value="200+">200+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="annual_budget">Annual Social Impact or Social Giving Budget *</Label>
                  <Select value={formData.annual_budget} onValueChange={(value) => setFormData({ ...formData, annual_budget: value })} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="<€10k">&lt;€10k</SelectItem>
                      <SelectItem value="€10k-€50k">€10k-€50k</SelectItem>
                      <SelectItem value="€50k-€250k">€50k-€250k</SelectItem>
                      <SelectItem value=">€250k">&gt;€250k</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Address */}
              <div className="bg-blue-50 rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-2 text-blue-700 font-semibold mb-4">
                  <MapPin className="w-5 h-5" />
                  <h3>Company Address</h3>
                </div>

                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Select value={formData.country} onValueChange={handleCountryChange} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(country => (
                        <SelectItem key={country.value} value={country.value} disabled={country.disabled}>{country.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="region">
                    {formData.country === "United States" ? "State" : 
                     formData.country === "Australia" ? "State/Territory" :
                     formData.country === "Canada" ? "Province" :
                     "County/Region"} *
                  </Label>
                  {showRegionInput ? (
                    <Input
                      id="region"
                      required
                      value={formData.region}
                      onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                      placeholder="Enter region"
                    />
                  ) : (
                    <Select value={formData.region} onValueChange={(value) => setFormData({ ...formData, region: value })} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        {regions.map(region => (
                          <SelectItem key={region} value={region}>{region}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div>
                  <Label htmlFor="city">City/Town *</Label>
                  <Input
                    id="city"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., Dublin"
                  />
                </div>

                <div>
                  <Label htmlFor="address">Full Address *</Label>
                  <Input
                    id="address"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Street address"
                  />
                </div>

                <div>
                  <Label htmlFor="postal_code">Postal/ZIP Code *</Label>
                  <Input
                    id="postal_code"
                    required
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="e.g., A82 XY12"
                  />
                </div>
              </div>

              {/* Consent and Declarations */}
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div className="font-semibold mb-4">
                  <h3>Consent and Declarations</h3>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="accurate"
                      checked={consents.accurate}
                      onCheckedChange={(checked) => setConsents({ ...consents, accurate: checked })}
                      required
                    />
                    <label htmlFor="accurate" className="text-sm leading-relaxed cursor-pointer">
                      I confirm that the information provided is accurate.
                    </label>
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="gdpr"
                      checked={consents.gdpr}
                      onCheckedChange={(checked) => setConsents({ ...consents, gdpr: checked })}
                      required
                    />
                    <label htmlFor="gdpr" className="text-sm leading-relaxed cursor-pointer">
                      I consent to PlayBack Token (Castleview Resources Ltd) storing and processing this data under GDPR.
                    </label>
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="noGuarantee"
                      checked={consents.noGuarantee}
                      onCheckedChange={(checked) => setConsents({ ...consents, noGuarantee: checked })}
                      required
                    />
                    <label htmlFor="noGuarantee" className="text-sm leading-relaxed cursor-pointer">
                      I understand that participation does not guarantee any funding or token allocation.
                    </label>
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="terms"
                      checked={consents.terms}
                      onCheckedChange={(checked) => setConsents({ ...consents, terms: checked })}
                      required
                    />
                    <label htmlFor="terms" className="text-sm leading-relaxed cursor-pointer">
                      I agree to the <Link to="/terms" className="text-red-600 hover:underline" target="_blank">PlayBack Token Terms & Conditions</Link> and <Link to="/privacy" className="text-red-600 hover:underline" target="_blank">Privacy Policy</Link>.
                    </label>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-center pt-4">
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-12 py-6 text-lg"
                >
                  {loading ? "Submitting..." : "Submit Application"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CompanyApplicationForm;
